import React, { useMemo, useState } from "react";
import classnames from "classnames";
import dayjs from "dayjs";
import FeatherIcon from "feather-icons-react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import PageEmptyData from "../../components/Common/PageEmptyData";
import { NOVEDADES } from "../../constants/novedades";
import NovedadDetalle from "./NovedadDetalle";
import { etiquetaPerfil, etiquetaTipo } from "./tipos";

require("dayjs/locale/es");
dayjs.locale("es");

/** Sin acentos y en minusculas: soporte teclea de prisa y sin acentuar. */
const normalizar = (valor) =>
  String(valor ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/** Busca el texto en todo lo que lleva un cambio, incluidas sus preguntas. */
const coincide = (cambio, texto) => {
  const campos = [
    cambio.titulo,
    cambio.descripcion,
    cambio.donde,
    cambio.accion,
    ...(cambio.perfiles ?? []),
    ...(cambio.faq ?? []).flatMap((f) => [f.pregunta, f.respuesta]),
  ];
  return campos.some((campo) => normalizar(campo).includes(texto));
};

/** Llave estable de un cambio: el id solo es unico dentro de su release. */
const llave = (novedad, cambio) => `${novedad.fecha}-${cambio.id}`;

/**
 * Novedades de la plataforma como material de consulta para soporte.
 *
 * Solo la ve el Reseller: la ruta se cierra con `policy: "routes"` en
 * allRoutes.js, igual que logs-errores y logs-wa.
 *
 * El contenido es estatico (constants/novedades.js), aqui no hay fetch.
 */
const Novedades = () => {
  const [busqueda, setBusqueda] = useState("");
  // El release mas reciente es el primero del array y abre por defecto.
  const [activeTab, setActiveTab] = useState(NOVEDADES[0]?.fecha ?? "");
  const [seleccionada, setSeleccionada] = useState("");

  const texto = normalizar(busqueda).trim();
  const buscando = texto.length > 0;

  // Buscando: todas las coincidencias de todos los releases. Si no, los
  // cambios del release del tab activo.
  const lista = useMemo(() => {
    const aplanar = (novedades) =>
      novedades.flatMap((novedad) =>
        (novedad.cambios ?? []).map((cambio) => ({ novedad, cambio }))
      );

    if (buscando) {
      return aplanar(NOVEDADES).filter((item) => coincide(item.cambio, texto));
    }
    return aplanar(NOVEDADES.filter((n) => n.fecha === activeTab));
  }, [buscando, texto, activeTab]);

  // La seleccion se deriva de la lista visible: si el elemento guardado ya no
  // esta (cambio de tab o de busqueda), cae al primero sin efectos de sincronia.
  const activa =
    lista.find((item) => llave(item.novedad, item.cambio) === seleccionada) ??
    lista[0];

  const releaseActivo = NOVEDADES.find((n) => n.fecha === activeTab);

  document.title = "Novedades | Sistema de logística";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Inicio" breadcrumbItem="Novedades" />

          {NOVEDADES.length === 0 ? (
            <PageEmptyData message="Aún no hay novedades publicadas." />
          ) : (
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                <Row className="align-items-center">
                  <Col md={7}>
                    <h5 className="mb-2 mb-md-0 font-size-15">
                      <FeatherIcon icon="gift" className="icon-sm me-2" />
                      Últimas actualizaciones de la plataforma
                    </h5>
                  </Col>
                  <Col md={5}>
                    <InputGroup>
                      <InputGroupText>
                        <FeatherIcon icon="search" className="icon-xs" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Buscar en todas las novedades"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </Row>

                {/* Buscando se recorre todo el historico, asi que los tabs por
                    fecha estorban. Con muchos releases la fila tiene que poder
                    hacer scroll horizontal o se desborda en pantallas chicas. */}
                {!buscando && (
                  <div className="overflow-auto">
                    <Nav
                      tabs
                      className="nav-tabs-custom card-header-tabs flex-nowrap mt-3"
                    >
                      {NOVEDADES.map((novedad) => (
                        <NavItem key={novedad.fecha}>
                          <NavLink
                            href="#"
                            className={classnames(
                              { active: activeTab === novedad.fecha },
                              "px-3 text-nowrap"
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveTab(novedad.fecha);
                            }}
                          >
                            {dayjs(novedad.fecha).format("DD MMM YYYY")}
                            {novedad.version && (
                              <span className="badge bg-light text-muted ms-2">
                                {novedad.version}
                              </span>
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  </div>
                )}
              </CardHeader>

              <CardBody>
                {buscando ? (
                  <p className="text-muted mb-3">
                    {lista.length === 1
                      ? "1 resultado"
                      : `${lista.length} resultados`}
                  </p>
                ) : (
                  releaseActivo?.resumen && (
                    <p className="text-muted mb-3">{releaseActivo.resumen}</p>
                  )
                )}

                {lista.length === 0 ? (
                  <PageEmptyData message="Ninguna novedad coincide con la búsqueda." />
                ) : (
                  <Row>
                    <Col md={4}>
                      <div
                        className="list-group"
                        style={{ maxHeight: "520px", overflowY: "auto" }}
                      >
                        {lista.map(({ novedad, cambio }) => {
                          const id = llave(novedad, cambio);
                          const tipo = etiquetaTipo(cambio.tipo);
                          const esActiva =
                            activa &&
                            llave(activa.novedad, activa.cambio) === id;
                          return (
                            <button
                              type="button"
                              key={id}
                              className={classnames(
                                "list-group-item list-group-item-action text-start",
                                { active: esActiva }
                              )}
                              onClick={() => setSeleccionada(id)}
                            >
                              <span
                                className={`badge bg-${tipo.color} mb-1 me-1`}
                              >
                                {tipo.label}
                              </span>
                              {(cambio.perfiles ?? []).map((perfil) => {
                                const p = etiquetaPerfil(perfil);
                                return (
                                  <span
                                    key={perfil}
                                    className={`badge bg-${p.color} mb-1 me-1`}
                                  >
                                    {p.label}
                                  </span>
                                );
                              })}
                              <span className="d-block">{cambio.titulo}</span>
                              {buscando && (
                                <small
                                  className={classnames("d-block", {
                                    "text-muted": !esActiva,
                                  })}
                                >
                                  {dayjs(novedad.fecha).format("DD MMM YYYY")}
                                </small>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </Col>
                    <Col md={8} className="mt-3 mt-md-0">
                      {activa && (
                        <NovedadDetalle
                          cambio={activa.cambio}
                          novedad={activa.novedad}
                        />
                      )}
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Novedades;
